// The rendered report for one submission (/submissions/view-report), reached
// from a Submissions row's REPORT icon.
//
// The report itself is NOT built here: GET submissions/report/:id/web returns
// the whole thing as server-rendered HTML, so this page is only the chrome
// around it — a sticky identity header and a floating action rail. Everything
// inside the scroll area is the server's markup, mounted verbatim.
//
// Param-less like /submissions and /form-templates/builder: the submission's
// identity rides in location.state and a stateless visit redirects at render
// time. It redirects to /reports rather than /submissions because the list
// needs its own formId state and would immediately bounce again.
//
// PDF report opens the same endpoint's `pdf` render in ReportPdfOverlay, in
// whatever language the page is currently set to. That overlay is presentational,
// so its Download PDF button is wired from here too — a third endpoint returning
// a presigned link, in the same language as the render on screen.
//
// The language button is a REPORT-content control, not an app-locale one: the
// endpoint's `:language` segment picks how the server renders the document, and
// the two locales are independent axes. Switching the app language from the user
// menu leaves the report exactly as it is (and fires no refetch); this toggle
// leaves the app's chrome exactly as it is. It starts at `en` on every mount.
//
// Jump to is live: the report response carries its own outline (`sections`,
// each `referenceId` a data-referenceid attribute on a section element in the
// mounted HTML), so the rail pill is a Dropdown over in-page scrolls —
// rendered only when the outline is non-empty (a jump menu over nothing is an
// inert affordance).
//
// Deliberate gaps, both pending backend contracts: Share in the header and
// the Comments rail action toast coming-soon (no route exists for a share
// link or comments). Workflow actions opens WorkflowActionsDrawer — the
// step listing and the transition itself are both live.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, Chip, Dropdown, IconButton } from '@/components/common';
import {
  SubmissionReportType,
  useGetSubmissionReportQuery,
  useLazyGetSubmissionPdfUrlQuery,
} from '@/api/endpoints/submissionsApi';
import type { ApiErrorShape } from '@/api/types';
import type { AppLanguage } from '@/redux/language/languageSlice';
import { useCanShareReports } from '@/hooks/useModuleAccess';
import { downloadFileUrl } from '@/utils/downloadUrl';
import { fmtDateTime, fmtSourceCode } from './submissionColumns';
import ReportPdfOverlay from './ReportPdfOverlay';
import WorkflowActionsDrawer from './WorkflowActionsDrawer';

interface ReportRouteState {
  submissionId?: string;
  /** The submission's own reference number — the `5640` half of the meta line. */
  referenceNumber?: number;
  /** The source record's reference number, absent when it has no source record. */
  sourceRef?: number | null;
  formName?: string;
  completeDate?: string;
  comments?: number;
}

const ReportViewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Verb gates: the Share stub rides the `reports` module's `sharing` extra
  // (Print/Download gate themselves inside ReportPdfOverlay on `export`). The
  // workflow-actions drawer is DELIBERATELY ungated — step transitions run on
  // their own backend-derived (assignee) permissions, not the admin modules.
  const canShare = useCanShareReports();
  const { submissionId, referenceNumber, sourceRef, formName, completeDate, comments } =
    (location.state as ReportRouteState | null) ?? {};

  // Seeded from a literal, never from `i18n.language`, and written only by the
  // toggle below — that independence is the point, so an English-speaking
  // reviewer can read an Arabic-language inspection without either locale
  // dragging the other along. Plain component state, so `en` reapplies on every
  // mount and the choice is per-visit.
  const [reportLang, setReportLang] = useState<AppLanguage>('en');
  // The Workflow actions drawer — its step fetch is skipped until first open.
  const [workflowOpen, setWorkflowOpen] = useState(false);
  // The rail's Jump to menu — a list of the report's own sections.
  const [jumpOpen, setJumpOpen] = useState(false);

  const {
    data: report,
    isLoading,
    isFetching,
    isError,
  } = useGetSubmissionReportQuery(
    { id: submissionId ?? '', reportType: SubmissionReportType.Web, language: reportLang },
    { skip: !submissionId },
  );

  // The pdf render is the SAME endpoint asked with a different reportType, so
  // it is fetched only once the overlay is first opened and RTK Query then
  // serves reopens from cache.
  const [pdfOpen, setPdfOpen] = useState(false);
  const {
    data: pdfReport,
    isFetching: pdfFetching,
    isError: pdfError,
  } = useGetSubmissionReportQuery(
    { id: submissionId ?? '', reportType: SubmissionReportType.Pdf, language: reportLang },
    { skip: !submissionId || !pdfOpen },
  );

  // Download is a different endpoint from the two renders above: it returns a
  // presigned link to a PDF the server has already built, so the app hands the
  // link to `downloadFileUrl` and never touches the file through the api layer.
  // Lazy, so the click drives it, and it follows `reportLang` — the file that
  // lands has to match the render on screen.
  //
  // The spinner rides on local state rather than the hook's `isFetching`, which
  // covers only the first of the two legs — it goes false the moment the LINK
  // arrives, while the bytes are still transferring.
  const [fetchPdfUrl] = useLazyGetSubmissionPdfUrlQuery();
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const url = await fetchPdfUrl({ id: submissionId ?? '', language: reportLang }).unwrap();
      await downloadFileUrl(url, `report-${referenceNumber ?? submissionId}-${reportLang}.pdf`);
    } catch (err) {
      // The endpoint's own failures carry a server message; downloadFileUrl's
      // throw does not, and falls through to the generic copy on purpose — a
      // storage status code is not something to show a user.
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    } finally {
      setDownloading(false);
    }
  };

  const soon = () => toast(t('text_coming_soon'));

  // Back through history rather than navigate('/submissions'): the list is also
  // param-less, so a fresh push would arrive without its formId and bounce to
  // /reports. Going back restores that entry's own state (and its scroll).
  const back = () => navigate(-1);

  const when = completeDate ? fmtDateTime(completeDate, t) : '';
  const meta =
    sourceRef === null || sourceRef === undefined
      ? t('report_view_meta_plain', { ref: referenceNumber, when })
      : t('report_view_meta', { code: fmtSourceCode(sourceRef), ref: referenceNumber, when });

  // No submission in the router state → nothing to render; leave before
  // anything paints, and `replace` so Back doesn't return here to bounce again.
  if (!submissionId) return <Navigate to='/reports' replace />;

  return (
    <div className='report-view'>
      <header className='report-view__bar'>
        <IconButton
          icon='arrow-left'
          variant='ghost'
          aria-label={t('report_view_back')}
          onClick={back}
        />
        <div className='report-view__identity'>
          <h1 className='report-view__title'>{formName}</h1>
          <p className='report-view__meta'>{meta}</p>
        </div>
        <div className='report-view__actions'>
          {/* Names the language it switches TO, not the one in view — the button
              labels its action, so an English report reads "Arabic". The hint
              rides on `title`, not `aria-label` — an aria-label would replace the
              visible text in the accessible name (WCAG 2.5.3). */}
          <Button
            variant='secondary'
            iconStart='languages'
            title={t('select_language')}
            onClick={() => setReportLang((prev) => (prev === 'en' ? 'ar' : 'en'))}
          >
            {t(reportLang === 'en' ? 'report_view_lang_ar' : 'report_view_lang_en')}
          </Button>
          <Button variant='secondary' iconStart='eye' onClick={() => setPdfOpen(true)}>
            {t('report_view_pdf')}
          </Button>
          {canShare && (
            <Button iconStart='share-2' onClick={soon}>
              {t('report_view_share')}
            </Button>
          )}
        </div>
      </header>

      {isLoading || isFetching ? (
        <div className='report-view__status'>{t('text_loading')}</div>
      ) : isError ? (
        <div className='report-view__status'>{t('error_generic')}</div>
      ) : (
        // The server owns every element below this line — it arrives as one
        // finished document, so nothing here parses, sanitises or reshapes it.
        // `lang`/`dir` declare what the content IS, which is the wrapper's job
        // and not the "pass nothing down" rule this container otherwise keeps
        // (no fonts, no colours). If the payload sets its own dir on a root
        // element, that wins on its own elements — verify against a real `ar`
        // response.
        <div
          className='report-view__html'
          lang={reportLang}
          dir={reportLang === 'ar' ? 'rtl' : 'ltr'}
          dangerouslySetInnerHTML={{ __html: report?.html ?? '' }}
        />
      )}

      {/* Fixed rail, inset-inline-end so it mirrors under RTL on its own. */}
      <div className='report-view__rail'>
        {/* Each referenceId is a data-referenceid ATTRIBUTE on the section's
            element inside the mounted report HTML (confirmed sample — the
            server sets no ids), so a jump is an attribute-selector lookup +
            scroll. Section names are the server's own copy, rendered
            literally. The rail is fixed, hence portal+flip (the menu opens
            upward from the bottom of the viewport). */}
        {(report?.sections.length ?? 0) > 0 && (
          <Dropdown
            open={jumpOpen}
            onClose={() => setJumpOpen(false)}
            align='end'
            width={240}
            portal
            flip
            trigger={
              <Button
                variant='secondary'
                shape='pill'
                iconStart='list'
                iconEnd={jumpOpen ? 'chevron-up' : 'chevron-down'}
                aria-haspopup='menu'
                aria-expanded={jumpOpen}
                onClick={() => setJumpOpen((o) => !o)}
              >
                {t('report_view_jump_to')}
              </Button>
            }
          >
            {report?.sections.map((section, i) => (
              <button
                key={section.referenceId}
                type='button'
                className='app-dropdown__item report-view__jump-item'
                role='menuitem'
                onClick={() => {
                  setJumpOpen(false);
                  document
                    .querySelector(`[data-referenceid="${CSS.escape(section.referenceId)}"]`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {/* The ordinal tile is decorative — the list order already
                    carries it for a screen reader. */}
                <span className='report-view__jump-num' aria-hidden='true'>
                  {i + 1}
                </span>
                <span className='report-view__jump-label'>{section.name}</span>
              </button>
            ))}
          </Dropdown>
        )}
        <Button variant='secondary' shape='pill' iconStart='message-square' onClick={soon}>
          {t('report_view_comments')}
          {comments !== undefined && comments > 0 && (
            <Chip variant='brand-solid' className='report-view__badge'>
              {comments}
            </Chip>
          )}
        </Button>
        <Button shape='pill' iconStart='workflow' onClick={() => setWorkflowOpen(true)}>
          {t('report_view_workflow')}
        </Button>
      </div>

      <WorkflowActionsDrawer
        open={workflowOpen}
        onClose={() => setWorkflowOpen(false)}
        submissionId={submissionId ?? ''}
        formName={formName}
      />

      <ReportPdfOverlay
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        html={pdfReport?.html}
        totalPages={pdfReport?.totalPages}
        lang={reportLang}
        loading={pdfFetching}
        error={pdfError}
        onDownload={downloadPdf}
        downloading={downloading}
      />
    </div>
  );
};

export default ReportViewPage;
